import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import { Input } from "../../../../components/ui/input";
import { Switch } from "../../../../components/ui/switch";
import FilterCondition from "../../../../shared/filter/FilterCondition";
import { useWorkspaceObjectSchema } from "../../../../hooks/QueryHooks";
import {
  getTaskFilterPreview,
  getTaskSettings,
  updateTaskSettings,
} from "../../../../services/tasksEndpoints";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import notify from "../../../../utils/notify";
import type { TaskSettings } from "../../tasks/tasksTypes";

// ------------------------------------------------------------------ //
// Types
// ------------------------------------------------------------------ //

interface FilterPredicate {
  entity_type: string;
  field: string;
  operator: string;
  value: any;
  type: string;
}

interface AccountFilters {
  query_type: "AND" | "OR";
  predicates: FilterPredicate[];
}

const EMPTY_FILTERS: AccountFilters = { query_type: "AND", predicates: [] };

const EMPTY_PREDICATE: FilterPredicate = {
  entity_type: "column",
  field: "",
  operator: "",
  value: "",
  type: "string",
};

// ------------------------------------------------------------------ //
// Main Page
// ------------------------------------------------------------------ //

const TaskSettingsPage = () => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const queryClient = useQueryClient();

  const [enabled, setEnabled] = useState(false);
  const [guidance, setGuidance] = useState("");
  const [autoCloseEnabled, setAutoCloseEnabled] = useState(false);
  const [autoCloseStaleDays, setAutoCloseStaleDays] = useState<number>(14);
  const [accountFilters, setAccountFilters] = useState<AccountFilters>(EMPTY_FILTERS);

  const { data: workspaceSchema } = useWorkspaceObjectSchema({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const settingsQuery = useQuery<TaskSettings | null>({
    queryKey: ["task-settings", workspace?.id],
    enabled: !!workspace?.id,
    queryFn: async () => {
      if (!workspace?.id) return null;
      try {
        return await getTaskSettings({ workspace_id: workspace.id });
      } catch {
        return null;
      }
    },
  });

  useEffect(() => {
    if (settingsQuery.data) {
      setEnabled(settingsQuery.data.enabled);
      setGuidance(settingsQuery.data.guidance || "");
      setAutoCloseEnabled(settingsQuery.data.auto_close_enabled);
      setAutoCloseStaleDays(settingsQuery.data.auto_close_stale_days || 14);
      if (settingsQuery.data.account_filters) {
        const af = settingsQuery.data.account_filters as AccountFilters;
        if (af.predicates && af.predicates.length > 0) {
          setAccountFilters(af);
        }
      }
    }
  }, [settingsQuery.data]);

  // Filter preview count
  const hasValidPredicates = useMemo(
    () =>
      accountFilters.predicates.length > 0 &&
      accountFilters.predicates.some(
        (p) =>
          p.field &&
          p.operator &&
          (["known", "unknown", "is_known", "is_unknown"].includes(p.operator) ||
            (p.value !== undefined && p.value !== null && p.value !== "")),
      ),
    [accountFilters],
  );

  const previewQuery = useQuery<{ matching_accounts: number } | null>({
    queryKey: ["task-filter-preview", workspace?.id, accountFilters],
    enabled: !!workspace?.id && hasValidPredicates,
    queryFn: async () => {
      if (!workspace?.id) return null;
      try {
        return await getTaskFilterPreview({
          workspace_id: workspace.id,
          account_filters: accountFilters,
        });
      } catch {
        return null;
      }
    },
    staleTime: 5000,
  });

  // Filter handlers
  const handleFilterChange = useCallback(
    (
      field: string,
      operator: string,
      value: any,
      type: string,
      entityType: string,
      id: number,
      _filterId: number,
    ) => {
      setAccountFilters((prev) => {
        const next = [...prev.predicates];
        next[id] = {
          entity_type: entityType || "column",
          field: field || "",
          operator: operator || "",
          value: value ?? "",
          type: type || "string",
        };
        return { ...prev, predicates: next };
      });
    },
    [],
  );

  const handleRemoveFilter = useCallback((index: number) => {
    setAccountFilters((prev) => ({
      ...prev,
      predicates: prev.predicates.filter((_, i) => i !== index),
    }));
  }, []);

  const handleAddFilter = useCallback(() => {
    setAccountFilters((prev) => ({
      ...prev,
      predicates: [...prev.predicates, { ...EMPTY_PREDICATE }],
    }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setAccountFilters(EMPTY_FILTERS);
  }, []);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!workspace?.id) throw new Error("No workspace");
      // Only include filters that have at least a field set
      const validPredicates = accountFilters.predicates.filter((p) => p.field);
      const filtersPayload =
        validPredicates.length > 0
          ? { ...accountFilters, predicates: validPredicates }
          : undefined;
      return updateTaskSettings({
        workspace_id: workspace.id,
        settings: {
          enabled,
          guidance: guidance || undefined,
          account_filters: filtersPayload,
          auto_close_enabled: autoCloseEnabled,
          auto_close_stale_days: autoCloseStaleDays,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-settings", workspace?.id] });
      notify({
        heading: "Settings saved",
        type: "success",
        messageList: ["Task settings have been updated."],
      });
    },
    onError: () => {
      notify({
        heading: "Failed to save settings",
        type: "failure",
        messageList: ["Could not update task settings. Please try again."],
      });
    },
  });

  if (settingsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const previewCount = hasValidPredicates
    ? previewQuery.data?.matching_accounts ?? null
    : null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Tasks</h2>
        <p className="text-sm text-muted-foreground">
          Configure how tasks are generated and managed for your workspace.
        </p>
      </div>

      {/* Enable / Disable */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Enable Tasks</CardTitle>
              <CardDescription>
                When enabled, ORA will automatically generate tasks from meetings,
                emails, and other activities.
              </CardDescription>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>
        </CardHeader>
      </Card>

      {/* Account Filters — uses the same FilterCondition as views/automations */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Account Filters</CardTitle>
          <CardDescription>
            Only generate tasks for activities associated with accounts matching
            these criteria. Leave empty to generate tasks for all accounts.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {accountFilters.predicates.map((filter, idx) => (
            <div key={`filter-row-${idx}`} className="flex items-start gap-2">
              {idx > 0 && (
                <span className="flex h-8 items-center text-xs font-medium text-muted-foreground uppercase w-10 shrink-0 justify-center">
                  AND
                </span>
              )}
              <div className={idx > 0 ? "flex-1" : "flex-1 ml-[48px]"}>
                <FilterCondition
                  filter={filter}
                  workspaceSchema={workspaceSchema}
                  entityRelation="accounts"
                  currentKey={idx}
                  filterId={0}
                  onCompleteFilter={handleFilterChange}
                  onRemove={handleRemoveFilter}
                />
              </div>
            </div>
          ))}

          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={handleAddFilter}
              className="flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80"
            >
              <Plus className="h-4 w-4" />
              Add condition
            </button>

            {previewCount !== null && (
              <span className="text-sm text-muted-foreground">
                {previewQuery.isFetching && (
                  <Loader2 className="inline h-3 w-3 animate-spin mr-1" />
                )}
                {previewCount.toLocaleString()} matching account
                {previewCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {accountFilters.predicates.length > 0 && (
            <div className="flex gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                Clear All
              </Button>
              <Button variant="outline" size="sm" onClick={() => saveMutation.mutate()}>
                Done
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Guidance */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Task Guidance</CardTitle>
          <CardDescription>
            Provide instructions or context for how ORA should generate and
            categorize tasks.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <textarea
            value={guidance}
            onChange={(e) => setGuidance(e.target.value)}
            placeholder="e.g., Focus on action items from customer calls. Prioritize tasks related to onboarding and integration issues."
            rows={4}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </CardContent>
      </Card>

      {/* Auto-close */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Auto-close Stale Tasks</CardTitle>
              <CardDescription>
                Automatically move tasks to &ldquo;Closed&rdquo; if they
                haven&apos;t been updated within the specified number of days.
              </CardDescription>
            </div>
            <Switch
              checked={autoCloseEnabled}
              onCheckedChange={setAutoCloseEnabled}
            />
          </div>
        </CardHeader>
        {autoCloseEnabled && (
          <CardContent>
            <div className="flex items-center gap-3">
              <label
                htmlFor="stale-days"
                className="text-sm text-muted-foreground whitespace-nowrap"
              >
                Close after
              </label>
              <Input
                id="stale-days"
                type="number"
                min={1}
                max={365}
                value={autoCloseStaleDays}
                onChange={(e) =>
                  setAutoCloseStaleDays(
                    Math.max(1, Math.min(365, Number(e.target.value) || 14)),
                  )
                }
                className="h-8 w-20 text-sm"
              />
              <span className="text-sm text-muted-foreground">
                days of inactivity
              </span>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="gap-2"
        >
          {saveMutation.isPending && (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default TaskSettingsPage;
